import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setPostHogUser } from "../config/posthog";
import { setSentryUser } from "../config/sentry";
import { useGetCurrentUserAndOrganizationsQuery } from "../graphql/generated/types.gen";

export const ACTIVE_ORGANIZATION_KEY = "activeOrganizationId";
const ACTIVE_ORG_CHANGE_EVENT = "activeOrganizationChange";

function readActiveOrganizationId(): string | null {
	try {
		return localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
	} catch {
		return null;
	}
}

function writeActiveOrganizationId(organizationId: string | null): void {
	try {
		if (organizationId) {
			localStorage.setItem(ACTIVE_ORGANIZATION_KEY, organizationId);
		} else {
			localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
		}
	} catch (error) {
		console.error("Failed to store active organization:", error);
	}
}

function publishActiveOrganization(organizationId: string | null): void {
	window.dispatchEvent(
		new CustomEvent(ACTIVE_ORG_CHANGE_EVENT, { detail: organizationId }),
	);
}

export function rememberActiveOrganization(organizationId: string): void {
	writeActiveOrganizationId(organizationId);
	publishActiveOrganization(organizationId);
}

export interface ActiveOrganization {
	id: string;
	name: string;
	websiteUrl?: string | null;
	role: string;
	nodeId?: string | null;
}

export function useActiveOrganization() {
	const {
		data: userData,
		loading,
		error,
		refetch,
	} = useGetCurrentUserAndOrganizationsQuery();
	const [activeOrganizationId, setActiveOrganizationId] = useState<
		string | null
	>(() => readActiveOrganizationId());
	const refetchedForId = useRef<string | null>(null);

	// Get the active organization object
	const activeOrganization = useMemo<ActiveOrganization | null>(() => {
		if (!userData?.organizations || !activeOrganizationId) return null;

		const org = userData.organizations.find(
			(org) => org.id === activeOrganizationId,
		);
		return org
			? {
					id: org.id,
					name: org.name,
					websiteUrl: org.websiteUrl,
					role: org.role,
					nodeId: org.nodeId,
				}
			: null;
	}, [userData?.organizations, activeOrganizationId]);

	// Set active organization and persist to localStorage
	const setActiveOrganization = useCallback((organizationId: string | null) => {
		setActiveOrganizationId(organizationId);
		if (organizationId) {
			rememberActiveOrganization(organizationId);
			return;
		}
		writeActiveOrganizationId(null);
		publishActiveOrganization(null);
	}, []);

	// Sync state across hook instances via custom event
	useEffect(() => {
		const handleOrgChange = (e: Event) => {
			const newOrgId = (e as CustomEvent<string | null>).detail;
			setActiveOrganizationId(newOrgId);
		};
		window.addEventListener(ACTIVE_ORG_CHANGE_EVENT, handleOrgChange);
		return () =>
			window.removeEventListener(ACTIVE_ORG_CHANGE_EVENT, handleOrgChange);
	}, []);

	// Set Sentry and PostHog user context when user data is loaded
	useEffect(() => {
		if (userData?.currentUser) {
			const user = {
				id: userData.currentUser.id,
				email: userData.currentUser.email ?? undefined,
			};
			setSentryUser(user);
			setPostHogUser(user);
		} else {
			setSentryUser(null);
			setPostHogUser(null);
		}
	}, [userData?.currentUser]);

	// Auto-select first organization if none selected or current selection is invalid
	useEffect(() => {
		// Don't run validation while data is still loading to avoid race conditions
		if (loading) return;

		// Always sync from localStorage first - it's the source of truth for explicit selections
		const storedId = readActiveOrganizationId();
		if (storedId && storedId !== activeOrganizationId) {
			setActiveOrganizationId(storedId);
			return;
		}

		if (!userData?.organizations?.length) {
			// No organizations - clear active organization
			if (activeOrganizationId) {
				setActiveOrganization(null);
			}
			return;
		}

		const hasValidSelection =
			activeOrganizationId &&
			userData.organizations.some((org) => org.id === activeOrganizationId);

		if (!hasValidSelection) {
			// If localStorage has a value but it's not in the org list, try refetching once
			// to ensure we have fresh data before giving up on this org
			if (storedId && refetchedForId.current !== storedId) {
				refetchedForId.current = storedId;
				refetch();
				return;
			}
			// After refetch, if still invalid, fall back to first org
			setActiveOrganization(userData.organizations[0].id);
		}
	}, [
		userData?.organizations,
		activeOrganizationId,
		setActiveOrganization,
		loading,
		refetch,
	]);

	// Switch to a specific organization
	const switchToOrganization = useCallback(
		(organizationId: string) => {
			const targetOrg = userData?.organizations?.find(
				(org) => org.id === organizationId,
			);
			if (targetOrg) {
				setActiveOrganization(organizationId);
			}
		},
		[userData?.organizations, setActiveOrganization],
	);

	// Check if a specific organization is active
	const isOrganizationActive = useCallback(
		(organizationId: string) => {
			return activeOrganizationId === organizationId;
		},
		[activeOrganizationId],
	);

	// Get all organizations for selection UI
	const allOrganizations = useMemo(() => {
		return (
			userData?.organizations?.map((org) => ({
				id: org.id,
				name: org.name,
				websiteUrl: org.websiteUrl,
				role: org.role,
			})) || []
		);
	}, [userData?.organizations]);

	// Flag indicating if we have a valid active organization
	const isReady = !loading && !!activeOrganization;

	return {
		activeOrganization,
		activeOrganizationId,
		setActiveOrganization,
		switchToOrganization,
		isOrganizationActive,
		allOrganizations,
		loading,
		error,
		refetch,
		isReady,
	};
}

// Hook for components that are guaranteed to render after the loading check
// These components can safely assume activeOrganization is not null
export function useActiveOrganizationRequired() {
	const result = useActiveOrganization();

	if (!result.activeOrganization) {
		throw new Error(
			"useActiveOrganizationRequired can only be used in components rendered after the loading check",
		);
	}

	return {
		...result,
		activeOrganization: result.activeOrganization as ActiveOrganization,
	};
}
